import { normalizePurchasingSettingsPermissions } from "./purchasingSettingsPermissions";

const membershipIds = (value) => {
  if (Array.isArray(value)) return value;
  try { const parsed = JSON.parse(value); return Array.isArray(parsed) ? parsed : []; } catch { return []; }
};
const ids = (values = []) => [...new Set(values.map(Number).filter((id) => Number.isInteger(id) && id > 0))];
export const managementHotel = (options, id) => options?.business_units?.find((hotel) => Number(hotel.id) === Number(id));
export const assignableRoles = (options, roles, hotelId) => roles.filter((role) =>
  managementHotel(options, hotelId)?.assignable_role_ids?.includes(Number(role.id)));

export function userAssignmentRows(user, options) {
  const rows = new Map((user?.role_assignments || []).map((row) => [Number(row.business_unit_id), {
    business_unit_id: String(row.business_unit_id), role_id: String(row.role_id || row.role?.id || ""),
  }]));
  const hotels = ids([...membershipIds(user?.business_unit_ids),
    ...(user?.has_access_to_all_business_units ? (options?.business_units || []).map((hotel) => hotel.id) : [])]);
  for (const id of hotels) if (!rows.has(id)) rows.set(id, { business_unit_id: String(id), role_id: String(user.role_id || "") });
  return [...rows.values()];
}

export function assignmentLocked(user, row, options) {
  if (!user) return false;
  return userAssignmentRows(user, options).some((original) => original.business_unit_id === String(row.business_unit_id))
    && !user.management?.editable_business_unit_ids?.includes(Number(row.business_unit_id));
}

export function assignmentsAllowed(options, rows, user = null) {
  if (!options || !rows.length || (user && user.management?.can_edit !== true)) return false;
  if (ids(rows.map((row) => row.business_unit_id)).length !== rows.length) return false;
  const original = userAssignmentRows(user, options);
  // Locked assignments must survive unchanged, including implicit legacy roles.
  if (original.some((row) => assignmentLocked(user, row, options) && !rows.some((next) =>
    Number(next.business_unit_id) === Number(row.business_unit_id) && Number(next.role_id) === Number(row.role_id)))) return false;
  // Switching the active hotel is an account-wide change.
  if (user && !user.management?.can_edit_profile && !rows.some((row) => Number(row.business_unit_id) === Number(user.active_business_unit_id))) return false;
  return rows.every((row) => {
    if (Number(row.role_id) <= 0 || !Number.isInteger(Number(row.role_id))) return false;
    // Existing assignments may outlive their role's assignable scope. Keeping
    // one unchanged is distinct from granting that role in another hotel.
    if (original.some((old) => Number(old.business_unit_id) === Number(row.business_unit_id)
      && Number(old.role_id) === Number(row.role_id))) return true;
    return managementHotel(options, row.business_unit_id)?.assignable_role_ids?.includes(Number(row.role_id)) === true;
  });
}

export function rolePermissionCeiling(options, selectedHotelIds, role = null) {
  const affected = ids([...selectedHotelIds, ...(role?.management?.permission_business_unit_ids || [])]);
  if (!options || !selectedHotelIds.length || !affected.length || affected.some((id) => !managementHotel(options, id)?.can_manage_roles)) return {};
  if (options.is_master_admin === true) return null; // Explicit server-confirmed master exemption.
  const permissions = affected.map((id) => normalizePurchasingSettingsPermissions(managementHotel(options, id)?.permissions));
  return Object.fromEntries(Object.keys(permissions[0]).filter((key) => permissions.every((entry) => entry[key] === true)).map((key) => [key, true]));
}

export function roleDraftAllowed(options, hotelIds, permissions, role = null) {
  if (!options || !hotelIds.length || (role && role.management?.can_edit !== true)) return false;
  if (hotelIds.some((id) => !managementHotel(options, id)?.can_manage_roles)) return false;
  const ceiling = rolePermissionCeiling(options, hotelIds, role);
  return ceiling === null || Object.entries(normalizePurchasingSettingsPermissions(permissions))
    .every(([key, value]) => !value || ceiling[key] === true);
}
